import { useHead, useSeoMeta } from '@unhead/vue'
import type { MaybeRefOrGetter } from 'vue'
import { toValue } from 'vue'

export interface SeoOptions {
  title: MaybeRefOrGetter<string>
  description: MaybeRefOrGetter<string>
  ogImage?: MaybeRefOrGetter<string>
  canonical?: MaybeRefOrGetter<string>
  noIndex?: MaybeRefOrGetter<boolean>
  locale?: MaybeRefOrGetter<string>
}

// Site URL: production (main) = itsh.dev, preview (dev) = preview.itsh.dev
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://itsh.dev'
const DEFAULT_OG_IMAGE = `${SITE_URL}/media/og-default.png`
const SITE_NAME = 'IT-Service Harting'

export { SITE_URL }

export function useSeo(options: SeoOptions) {
  useHead({
    title: () => toValue(options.title),
    link: options.canonical
      ? [
          {
            rel: 'canonical',
            href: () => toValue(options.canonical)
          }
        ]
      : undefined,
    meta: options.noIndex
      ? [
          {
            name: 'robots',
            content: () => (toValue(options.noIndex) ? 'noindex, nofollow' : 'index, follow')
          }
        ]
      : undefined
  })

  useSeoMeta({
    description: () => toValue(options.description),
    ogTitle: () => toValue(options.title),
    ogDescription: () => toValue(options.description),
    ogImage: () => toValue(options.ogImage) || DEFAULT_OG_IMAGE,
    ogUrl: () => toValue(options.canonical) || undefined,
    ogSiteName: SITE_NAME,
    ogLocale: () => toValue(options.locale) || 'de_DE',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: () => toValue(options.title),
    twitterDescription: () => toValue(options.description),
    twitterImage: () => toValue(options.ogImage) || DEFAULT_OG_IMAGE
  })
}

export function useProductSeo(options: {
  name: MaybeRefOrGetter<string>
  description: MaybeRefOrGetter<string>
  price?: MaybeRefOrGetter<string>
  ogImage?: MaybeRefOrGetter<string>
}) {
  const title = () => `${toValue(options.name)} | ${SITE_NAME}`

  useSeo({
    title,
    description: options.description,
    ogImage: options.ogImage
  })
}
